import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '@features/PageHeader';
import PageContainer from '@components/Layout/PageContainer';
import Paywall from '@features/Paywall';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import { useTranslation, trackExport } from '@hooks';
import { alertActions, couponsActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { removeFirstOccurrence } from '@helpers/arrays';
import { clampPageAfterRemoval } from '@helpers/pagination';
import { couponsService } from '@services';
import { ModalType } from '@constants/modal';
import { CouponStatus } from '@constants/coupons';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import usePermissions from '@hooks/usePermissions';
import { CouponIcon } from 'assets/images/icons';
import { BILLING_TYPE_MAP } from '@constants/billing';
import EditModal from './EditModal';
import CouponsTable from './CouponsTable';
import { getPlanName } from '@helpers/billing';

const CouponsPage = () => {
    const { t } = useTranslation();

    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [modal, setModal] = useState({ open: false, type: ModalType.Create });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [statusPendingIds, setStatusPendingIds] = useState([]);

    const selectedCouponRef = useRef({});

    const account = useSelector(state => state.account?.account);
    const companies = useSelector(state => state.account?.account?.companies || []);
    const { hasFullAccess } = usePermissions();
    const brands = useSelector(state => state.brands.data);
    const { totalCount } = useSelector(state => state.coupons);

    const isFreemiumPlan = getPlanName(account) === BILLING_TYPE_MAP.FREEMIUM;

    const dispatch = useDispatch();

    useEffect(() => {
        if (isFreemiumPlan || !account?.couponsEnabled) {
            return;
        }
        dispatch(couponsActions.get(page, rowsPerPage));
    }, [page, rowsPerPage, dispatch]);

    const onClickCoupon = (coupon, type) => {
        // pause/resume carry the coupon directly, so they must not disturb a selection
        // a concurrently open modal still owns
        if (type !== 'pause' && type !== 'resume') {
            setSelectedCoupon(coupon);
            selectedCouponRef.current = coupon;
        }
        switch (type) {
            case 'archive':
                setDeleteModalOpen(true);
                break;
            case 'edit':
                setModal({ open: true, type: ModalType.Edit });
                break;
            case 'view':
                setModal({ open: true, type: ModalType.View });
                break;
            case 'pause':
                onModifyCoupon('pause', coupon);
                break;
            case 'resume':
                onModifyCoupon('resume', coupon);
                break;
            case 'download':
                onDownloadCoupon(coupon);
                break;
            default:
                break;
        }
    };

    const AlertMessagesMap = {
        archive: 'delete',
        pause: 'edit',
        resume: 'edit',
    };

    const StatusMachineMap = {
        pause: CouponStatus.Paused,
        resume: CouponStatus.Active,
    };

    const onModifyCoupon = async (operation, coupon = selectedCouponRef.current) => {
        const couponId = coupon?.id;
        setStatusPendingIds(prev => [...prev, couponId]);
        try {
            await couponsService.updateCouponStatus(couponId, operation);
            if (operation === 'archive') {
                setDeleteModalOpen(false);
                const targetPage = clampPageAfterRemoval(page, totalCount, rowsPerPage);
                setPage(targetPage);
                dispatch(couponsActions.get(targetPage, rowsPerPage));
                dispatch(alertActions.success(t(`alertMessages.${AlertMessagesMap[operation]}Success`)));
                return;
            }
            dispatch(couponsActions.updateStatus(couponId, StatusMachineMap[operation]));
            dispatch(couponsActions.resetAll());
            dispatch(alertActions.success(t(`alertMessages.${AlertMessagesMap[operation]}Success`)));
        } catch (error) {
            handleApiError(error, `alertMessages.${AlertMessagesMap[operation]}Fail`, { amount: error.count });
        } finally {
            // Remove one occurrence, not all — pause and archive can both hold a slot for
            // the same coupon, and the first to settle must not free the other's slot.
            setStatusPendingIds(prev => removeFirstOccurrence(prev, couponId));
        }
    };

    const onCouponSaved = () => {
        dispatch(couponsActions.resetAll());
        const targetPage = modal.type === ModalType.Create ? 0 : page;
        if (targetPage === page) {
            dispatch(couponsActions.get(targetPage, rowsPerPage));
        } else {
            setPage(targetPage); // INFO: triggers the refetch effect
        }
    };

    const onAddCouponClick = () => {
        setSelectedCoupon(null);
        selectedCouponRef.current = {};
        setModal({ open: true, type: ModalType.Create });
    };

    const onDownloadCoupon = async () => {
        try {
            const response = await couponsService.exportCSV({
                couponTemplateId: selectedCouponRef.current.id,
            });
            if (response?.exportStatusId) {
                trackExport(response.exportStatusId);
                dispatch(
                    alertActions.success(t('exports.exporting'), { id: response.exportStatusId, persistent: true }),
                );
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    return (
        <PageContainer>
            {isFreemiumPlan || !account?.couponsEnabled ? (
                <Paywall />
            ) : (
                <>
                    <PageHeader
                        styles={{ marginBottom: '16px' }}
                        icon={<CouponIcon />}
                        title={t('CouponsPage.title')}
                        subtitle={t('CouponsPage.subtitle')}
                    >
                        {hasFullAccess('coupons') && (
                            <Button variant="contained" onClick={onAddCouponClick}>
                                {t('CouponsPage.createNewCoupon')}
                            </Button>
                        )}
                    </PageHeader>
                    <CouponsTable
                        companies={companies}
                        brands={brands}
                        setPage={setPage}
                        page={page}
                        setRowsPerPage={setRowsPerPage}
                        rowsPerPage={rowsPerPage}
                        onClickCoupon={onClickCoupon}
                        statusPendingIds={statusPendingIds}
                    />
                    {modal.open ? (
                        <EditModal
                            modal={modal}
                            setModal={setModal}
                            brands={brands}
                            selectedCoupon={selectedCoupon}
                            setSelectedCoupon={setSelectedCoupon}
                            onSaved={onCouponSaved}
                        />
                    ) : null}
                    {isDeleteModalOpen && (
                        <ModalConfirm
                            isOpen={isDeleteModalOpen}
                            handleClose={() => setDeleteModalOpen(false)}
                            title={t('common.deleteTitle')}
                            description={t('common.deleteDescription')}
                            onPrimaryAction={() => onModifyCoupon('archive')}
                            primaryActionText={t('buttons.delete')}
                            primaryActionType="danger"
                        />
                    )}
                </>
            )}
        </PageContainer>
    );
};

export default CouponsPage;
